import { useState } from "react";
import {
  Settings,
  FileText,
  ClipboardList,
  ChevronDown,
  Cog,
  BarChart3,
  Phone,
  Users,
  Database,
  Receipt,
  ListChecks,
  Package,
  PieChart,
  MessageSquare,
  Link,
  Building,
  CheckCircle,
  Eye,
  Info,
} from "lucide-react";
import { Button } from "@shared/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@shared/ui/tabs";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@shared/ui/dropdown-menu";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@shared/ui/table";
import FilterSidebar from "./components/FilterSidebar";
import IdTable, { IdConfig } from "./components/IdTable";
import CollapsibleIdConfigForm from "./components/CollapsibleIdConfigForm";
import LogsModal from "./components/LogsModal";
import FormatDetailsModal from "./components/FormatDetailsModal";
import { Card, CardContent, CardDescription, CardTitle } from "@shared/ui/card";
import AuditLogFilters from "./components/AuditLogFilterPanel";
import AuditLogDetails, { AuditLogEntry } from "./components/AuditLogDetails";
import ContextualHelp from "./components/ContextualHelp";
import { useToast } from "@shared/hooks/use-toast";
import { NumberFormat } from "../../types";
import { useNumberFormats } from "../../hooks";
import { AuditLogsTable } from "./components/AuditLogTable";
const idConfigs = [
  {
    id: "1",
    name: "Customer ID",
    type: "master" as const,
    entity: "Customer",
    format: "WA-CUST-202404-0001",
    lastModified: "2024-04-02",
    lastModifiedBy: "Alex Smith",
  },
  {
    id: "2",
    name: "Meter ID",
    type: "master" as const,
    entity: "Meter",
    format: "WA-MTR-202404-0001",
    lastModified: "2024-04-01",
    lastModifiedBy: "Jordan Lee",
  },
  {
    id: "3",
    name: "Bill Number",
    type: "transaction" as const,
    entity: "Bill",
    format: "EL-BILL-202404-0001",
    lastModified: "2024-04-03",
    lastModifiedBy: "Casey Johnson",
  },
  {
    id: "4",
    name: "Payment Reference",
    type: "transaction" as const,
    entity: "Payment",
    format: "GA-PAY-202404-0001",
    lastModified: "2024-04-05",
    lastModifiedBy: "Taylor Wilson",
  },
];

// Sample audit log entries
const auditLogEntries: AuditLogEntry[] = [
  {
    id: "1",
    action: "Updated",
    idName: "Customer ID",
    entity: "Customer",
    modifiedBy: "Alex Smith",
    date: "2024-04-02",
    time: "10:45 AM",
    details: "Format updated",
    format: "WA-CUST-202404-0001",
    changes: [
      { field: "Prefix", oldValue: "CUST", newValue: "WA-CUST" },
      { field: "Sequence Length", oldValue: "3", newValue: "4" },
    ],
  },
  {
    id: "2",
    action: "Updated",
    idName: "Meter ID",
    entity: "Meter",
    modifiedBy: "Jordan Lee",
    date: "2024-04-01",
    time: "02:30 PM",
    details: "Format updated",
    format: "WA-MTR-202404-0001",
    changes: [{ field: "Date Element", oldValue: "YY", newValue: "YYYYMM" }],
  },
  {
    id: "3",
    action: "Updated",
    idName: "Bill Number",
    entity: "Bill",
    modifiedBy: "Casey Johnson",
    date: "2024-04-03",
    time: "09:15 AM",
    details: "Format updated",
    format: "EL-BILL-202404-0001",
    changes: [{ field: "Utility", oldValue: "WA", newValue: "EL" }],
  },
  {
    id: "4",
    action: "Created",
    idName: "Payment ID",
    entity: "Payment",
    modifiedBy: "Morgan Bailey",
    date: "2024-04-10",
    time: "02:34 PM",
    details: "New format created",
    format: "WA-PAY-202404-0001",
  },
  {
    id: "5",
    action: "Deleted",
    idName: "Old Customer Format",
    entity: "Customer",
    modifiedBy: "Admin User",
    date: "2024-04-08",
    time: "11:15 AM",
    details: "Format removed from system",
    format: "CUST-2024-0001",
  },
];

const menuItems = [
  {
    icon: Cog,
    label: "Utility Setup",
    description: "Configure core utility settings and parameters",
  },
  {
    icon: BarChart3,
    label: "Plan & Tariff",
    description: "Manage pricing plans and tariff structures",
  },
  {
    icon: Phone,
    label: "Call Center",
    description: "Customer support and call management",
  },
  {
    icon: Users,
    label: "Consumer Services",
    description: "Customer account and service management",
  },
  {
    icon: Database,
    label: "Meter Data",
    description: "Meter reading and data collection",
  },
  {
    icon: Receipt,
    label: "Billing",
    description: "Invoice generation and payment processing",
  },
  {
    icon: ListChecks,
    label: "Service Orders",
    description: "Work order and service request tracking",
  },
  {
    icon: Package,
    label: "Asset Management",
    description: "Infrastructure and equipment tracking",
  },
  {
    icon: PieChart,
    label: "Reports & Analytics",
    description: "Business intelligence and reporting",
  },
  {
    icon: MessageSquare,
    label: "Communication Hub",
    description: "Customer and internal communications",
  },
  {
    icon: Link,
    label: "Integration Hub",
    description: "System integrations and interfaces",
  },
  {
    icon: Building,
    label: "Organisation Setup",
    description: "Company structure and user management",
  },
];

const IdConfigLanding = () => {
  const { toast } = useToast();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isLogsModalOpen, setIsLogsModalOpen] = useState(false);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | undefined>(undefined);
  const [currentLogId, setCurrentLogId] = useState<string | undefined>(
    undefined
  );
  const [currentDetailId, setCurrentDetailId] = useState<string | undefined>(
    undefined
  );
  const [showConfigForm, setShowConfigForm] = useState(false);
  const [selectedIdType, setSelectedIdType] = useState<
    "master" | "transaction" | ""
  >("");

  // ADD THIS - Missing activeTab state
  const [activeTab, setActiveTab] = useState<"all" | "master" | "transaction">(
    "all"
  );

  // Audit log related states
  const [isAuditLogDetailsOpen, setIsAuditLogDetailsOpen] = useState(false);
  const [selectedAuditLog, setSelectedAuditLog] =
    useState<AuditLogEntry | null>(null);
  const [filteredAuditLogs, setFilteredAuditLogs] =
    useState<AuditLogEntry[]>(auditLogEntries);

  const [selectedRowData, setSelectedRowData] = useState<NumberFormat | null>(
    null
  );
  const [editingRowData, setEditingRowData] = useState<NumberFormat | null>(
    null
  );

  // Now this line will work because activeTab is declared
  const { data: numberFormatResponse, isLoading } = useNumberFormats(activeTab);
  const numberFormatData: NumberFormat[] = numberFormatResponse?.result || [];

  const handleTypeSelection = (type: any) => {
    setSelectedIdType(type);
    setEditingRowData(null); // Clear editing data when creating new
    setShowConfigForm(true);
  };

  const handleEdit = (id: string) => {
    // Find the complete row data
    const rowData = numberFormatData.find((item) => item.id.toString() === id);
    if (rowData) {
      setSelectedIdType(
        rowData.configurationType === 0 ? "master" : "transaction"
      );
      setEditingRowData(rowData); // Pass complete data instead of just ID
      setShowConfigForm(true);
    }
  };

  const handleViewLogs = (id: string) => {
    setCurrentLogId(id);
    setIsLogsModalOpen(true);
  };

  const handleViewDetails = (id: string) => {
    // Find the complete row data
    const rowData = numberFormatData.find((item) => item.id.toString() === id);
    if (rowData) {
      setSelectedRowData(rowData); // Pass complete data
      setIsDetailsModalOpen(true);
    }
  };

  const handleCloseConfigForm = () => {
    setShowConfigForm(false);
    setEditingRowData(null);
  };

  const handleCloseLogsModal = () => {
    setIsLogsModalOpen(false);
    setCurrentLogId(undefined);
  };

  const handleCloseDetailsModal = () => {
    setIsDetailsModalOpen(false);
    setSelectedRowData(null);
  };

  const handleViewAuditLogDetails = (logEntry: AuditLogEntry) => {
    setSelectedAuditLog(logEntry);
    setIsAuditLogDetailsOpen(true);
  };

  const handleCloseAuditLogDetails = () => {
    setIsAuditLogDetailsOpen(false);
    setSelectedAuditLog(null);
  };

  const handleAuditLogFilterChange = (filters: any) => {
    // Apply filters to audit logs
    let filtered = [...auditLogEntries];

    if (filters.searchTerm) {
      const searchLower = filters.searchTerm.toLowerCase();
      filtered = filtered.filter(
        (log) =>
          log.idName.toLowerCase().includes(searchLower) ||
          log.entity.toLowerCase().includes(searchLower) ||
          log.modifiedBy.toLowerCase().includes(searchLower) ||
          log.details.toLowerCase().includes(searchLower)
      );
    }

    if (filters.action && filters.action !== "all") {
      filtered = filtered.filter(
        (log) => log.action.toLowerCase() === filters.action.toLowerCase()
      );
    }

    if (filters.entity && filters.entity !== "all") {
      filtered = filtered.filter(
        (log) => log.entity.toLowerCase() === filters.entity.toLowerCase()
      );
    }

    if (filters.user && filters.user !== "all") {
      const users = {
        alex: "Alex Smith",
        jordan: "Jordan Lee",
        casey: "Casey Johnson",
        morgan: "Morgan Bailey",
      };
      const userName = users[filters.user as keyof typeof users];
      filtered = filtered.filter((log) => log.modifiedBy === userName);
    }

    if (filters.dateFrom) {
      filtered = filtered.filter((log) => {
        const logDate = new Date(log.date);
        return logDate >= filters.dateFrom;
      });
    }

    if (filters.dateTo) {
      filtered = filtered.filter((log) => {
        const logDate = new Date(log.date);
        return logDate <= filters.dateTo;
      });
    }

    setFilteredAuditLogs(filtered);

    toast({
      title: "Filters Applied",
      description: `Showing ${filtered.length} of ${auditLogEntries.length} log entries`,
    });
  };

  const currentIdConfig = idConfigs.find(
    (config) => config.id === currentLogId
  );

  const currentDetailConfig = idConfigs.find(
    (config) => config.id === currentDetailId
  );

  return (
    <div className="flex flex-col overflow-hidden">
  
       <div>
        <p className="page-title">ID & Reference Format Settings</p>
        <p className="page-subtitle">
          Configure format settings for entities, customers, and  transactions
        </p>
      </div>


      <div className="flex flex-1 overflow-hidden">
        <main className="flex-1 flex flex-col overflow-hidden">
          <div className="flex flex-col h-full">
            <Tabs
              defaultValue="format-config"
              className="w-full mt-4"
              tabsListClassName="bg-muted w-full flex justify-start rounded-lg mb-6"
              tabComponents={{
                "format-config": {
                  label: (
                    <span className="flex items-center gap-2">
                      <FileText className="h-4 w-4" />
                      Format Configuration
                    </span>
                  ),
                  component: (
                    <div className="flex flex-col gap-4 flex-1">
                      <div>
                        <h2 className="page-subheading pb-4">
                          Select ID Configuration Type
                        </h2>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          {["master", "transaction"].map((type) => (
                            <Card
                              key={type}
                              className={`cursor-pointer border-2 transition-all hover:shadow-md ${
                                selectedIdType === type
                                  ? "border-primary"
                                  : "border-gray-200"
                              }`}
                              onClick={() => handleTypeSelection(type)}
                            >
                              <CardContent className="pt-0">
                                <div className="flex justify-between items-start">
                                  <div>
                                    <CardTitle className="text-xl mb-2">
                                      {type === "master"
                                        ? "Master ID"
                                        : "Transaction ID"}
                                    </CardTitle>
                                    <CardDescription className="text-sm text-gray-600">
                                      {type === "master"
                                        ? "For core utility entities that remain relatively stable like customers, meters, and locations."
                                        : "For recurring operational records like bills, payments, and service requests that are created frequently."}
                                    </CardDescription>
                                  </div>
                                  {selectedIdType === type && (
                                    <CheckCircle className="h-5 w-5 text-primary" />
                                  )}
                                </div>
                              </CardContent>
                            </Card>
                          ))}
                        </div>
                      </div>

                      {showConfigForm && (
                        <CollapsibleIdConfigForm
                          editingData={editingRowData}
                          // editingId={editingId}
                          onClose={handleCloseConfigForm}
                          selectedIdType={selectedIdType}
                        />
                      )}

                      <div className="flex-1 overflow-auto">
                        <IdTable
                          onEdit={handleEdit}
                          onViewLogs={handleViewLogs}
                          onViewDetails={handleViewDetails}
                          activeTab={activeTab} // Pass activeTab to IdTable
                          onTabChange={setActiveTab} // Pass tab change handler
                        />
                      </div>
                    </div>
                  ),
                  className: "flex-1 flex flex-col",
                },
                "audit-logs": {
                  label: (
                    <span className="flex items-center gap-2">
                      <ClipboardList className="h-4 w-4" />
                      Audit Logs
                      <ContextualHelp content="Audit logs track all changes made to ID configurations, including who made the changes and when." />
                    </span>
                  ),
                  component: <AuditLogsTable />,
                  className: "flex-1 flex flex-col",
                },
              }}
            />
          </div>
        </main>
      </div>

      {currentIdConfig && (
        <LogsModal
          isOpen={isLogsModalOpen}
          onClose={handleCloseLogsModal}
          idName={currentIdConfig.name}
        />
      )}

      <FormatDetailsModal
        isOpen={isDetailsModalOpen}
        onClose={handleCloseDetailsModal}
        idConfig={currentDetailConfig}
        formatData={selectedRowData}
      />

      <AuditLogDetails
        isOpen={isAuditLogDetailsOpen}
        onClose={handleCloseAuditLogDetails}
        logEntry={selectedAuditLog}
      />
    </div>
  );
};

export default IdConfigLanding;
